import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import Button from '../common/Button';
import Typography from '../common/Typography';
import { Modal, ModalContent, ModalCloseButton, MobileSearchField } from '../common';
import { 
  addGuestPlayer, 
  addClubPlayerToSession, 
  addPlayerByEmail,
  getAvailableClubPlayers 
} from '../../services/privateSessionService';

const AddPlayerModal = ({ isOpen, onClose, onAddPlayer, sessionId, currentUser }) => {
  const [activeTab, setActiveTab] = useState('signedup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Club players tab
  const [clubPlayers, setClubPlayers] = useState([]);
  const [clubSearchTerm, setClubSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [playersPerPage] = useState(10);
  
  // Players signed up tab
  const [signedUpPlayers, setSignedUpPlayers] = useState([]);
  
  // Guest tab
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    gender: 'male'
  });
  
  // Email tab
  const [emailSearch, setEmailSearch] = useState('');
  const [emailResult, setEmailResult] = useState(null);

  // Filtered and paginated club players
  const filteredClubPlayers = clubPlayers.filter(player => {
    const searchTerm = clubSearchTerm.toLowerCase();
    return (
      (player.first_name?.toLowerCase() || '').includes(searchTerm) ||
      (player.last_name?.toLowerCase() || '').includes(searchTerm) ||
      (player.email?.toLowerCase() || '').includes(searchTerm) ||
      (player.club_name?.toLowerCase() || '').includes(searchTerm)
    );
  }).sort((a, b) => {
    // Sort alphabetically by first name, then last name
    const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
    const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const totalPages = Math.ceil(filteredClubPlayers.length / playersPerPage);
  const startIndex = (currentPage - 1) * playersPerPage;
  const endIndex = startIndex + playersPerPage;
  const paginatedClubPlayers = filteredClubPlayers.slice(startIndex, endIndex);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [clubSearchTerm]);

  // Load club players
  const loadClubPlayers = useCallback(async () => {
    try {
      const players = await getAvailableClubPlayers(currentUser.id, sessionId);
      setClubPlayers(players);
    } catch (err) {
      console.error('Error loading club players:', err);
      setError('Failed to load club players');
    }
  }, [currentUser.id, sessionId]);

  // Load signed up players
  const loadSignedUpPlayers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('session_players')
        .select(`
          player_id,
          players!inner(
            id,
            first_name,
            last_name,
            email,
            gender,
            is_guest,
            guest_created_by
          )
        `)
        .eq('session_id', sessionId)
        .eq('status', 'active');

      if (error) throw error;
      setSignedUpPlayers(data || []);
    } catch (err) {
      console.error('Error loading signed up players:', err);
      setError('Failed to load signed up players');
    }
  }, [sessionId]);

  // Load data when tab is active
  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'club') {
        loadClubPlayers();
      } else if (activeTab === 'signedup') {
        loadSignedUpPlayers();
      }
    }
  }, [activeTab, isOpen, loadClubPlayers, loadSignedUpPlayers]);

  // Handle club player selection
  const handleClubPlayerSelect = useCallback(async (playerId) => {
    try {
      setLoading(true);
      setError(null);
      
      const player = clubPlayers.find(p => p.id === playerId);
      await onAddPlayer(player, 'club');
      setSuccess('Player added successfully!');
      
      // Refresh the data instead of closing
      await loadClubPlayers();
      await loadSignedUpPlayers();
      
      setTimeout(() => {
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to add player');
    } finally {
      setLoading(false);
    }
  }, [clubPlayers, onAddPlayer, loadClubPlayers, loadSignedUpPlayers]);

  // Handle guest player creation
  const handleGuestSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!guestForm.firstName.trim() || !guestForm.lastName.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await onAddPlayer(guestForm, 'guest');
      setSuccess('Guest player added successfully!');
      setGuestForm({ firstName: '', lastName: '', gender: 'male' });
      
      // Refresh the data instead of closing
      await loadSignedUpPlayers();
      
      setTimeout(() => {
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to add guest player');
    } finally {
      setLoading(false);
    }
  }, [guestForm, onAddPlayer, loadSignedUpPlayers]);

  // Handle email search
  const handleEmailSearch = useCallback(async () => {
    if (!emailSearch.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { data: player, error } = await supabase
        .from('players')
        .select('*')
        .eq('email', emailSearch.trim())
        .not('auth_id', 'is', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          setError('No player found with this email');
        } else {
          throw error;
        }
      } else {
        setEmailResult(player);
      }
    } catch (err) {
      setError(err.message || 'Failed to search for player');
    } finally {
      setLoading(false);
    }
  }, [emailSearch]);

  // Handle email player addition
  const handleEmailPlayerAdd = useCallback(async () => {
    if (!emailResult) return;

    try {
      setLoading(true);
      setError(null);
      
      await onAddPlayer(emailResult, 'email');
      setSuccess('Player added successfully!');
      
      // Refresh the data instead of closing
      await loadSignedUpPlayers();
      
      setTimeout(() => {
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to add player');
    } finally {
      setLoading(false);
    }
  }, [emailResult, onAddPlayer, loadSignedUpPlayers]);


  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between border-b border-border-medium p-4 sm:p-6">
          <Typography variant="h5" className="font-semibold">Add Player</Typography>
          <ModalCloseButton onClose={onClose} className="static" />
        </div>

        {/* Tabs */}
        <div className="border-b border-border-medium">
          <div className="flex">
            <button
              onClick={() => setActiveTab('signedup')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'signedup'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Players Signed Up
            </button>
            <button
              onClick={() => setActiveTab('club')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'club'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Club Players
            </button>
            <button
              onClick={() => setActiveTab('guest')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'guest'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Add Guest
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'email'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Search by Email
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {/* Players Signed Up Tab */}
          {activeTab === 'signedup' && (
            <div>
              <div className="mb-4">
                <Typography variant="body" className="text-text-secondary">
                  Players already signed up for this session ({signedUpPlayers.length})
                </Typography>
              </div>
              
              <div className="space-y-2">
                {signedUpPlayers.length > 0 ? (
                  signedUpPlayers.map((sessionPlayer) => {
                    const player = sessionPlayer.players;
                    return (
                      <div key={player.id} className="flex items-center justify-between p-3 rounded-lg border border-success-dark bg-success-light">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {(player.first_name?.charAt(0) || '')}{(player.last_name?.charAt(0) || '')}
                          </div>
                          <div>
                            <Typography variant="body" className="font-medium">
                              {player.first_name || ''} {player.last_name || ''}
                            </Typography>
                            <Typography variant="caption" className="text-text-muted">
                              {player.is_guest ? 'Guest Player' : 'Club Member'}
                            </Typography>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-1 bg-success-light text-success-dark text-xs rounded-full border border-success-dark">
                            ✓ Signed Up
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Typography variant="body" className="text-text-muted">
                      No players signed up yet
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Club Players Tab */}
          {activeTab === 'club' && (
            <div>
              <div className="mb-4">
                <MobileSearchField
                  placeholder="Search club players..."
                  value={clubSearchTerm}
                  onChange={(e) => setClubSearchTerm(e.target.value)}
                  className="w-full form-input focus-ring"
                />
              </div>
              
              <div className="space-y-2">
                {paginatedClubPlayers.map((player) => (
                  <div key={player.id} className="flex items-center justify-between p-3 bg-surface-subtle rounded-lg border border-border-light">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Typography variant="body" className="font-medium">
                          {player.first_name || ''} {player.last_name || ''}
                        </Typography>
                        {player.is_guest && (
                          <span className="px-2 py-1 bg-warning-light text-warning-dark text-xs rounded-full border border-warning-dark">
                            Guest
                          </span>
                        )}
                      </div>
                      <Typography variant="caption" className="text-text-muted block">
                        {player.email || 'No email'}
                      </Typography>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="px-2 py-1 bg-info-light text-info text-xs rounded-full border border-border-medium">
                          {player.club_name || 'Unknown Club'}
                        </span>
                      </div>
                    </div>
                    <Button
                      size="small"
                      onClick={() => handleClubPlayerSelect(player.id)}
                      disabled={loading}
                    >
                      Add
                    </Button>
                  </div>
                ))}
                
                {paginatedClubPlayers.length === 0 && (
                  <div className="text-center py-8">
                    <Typography variant="body" className="text-text-muted">
                      {clubSearchTerm ? 'No players found matching your search' : 'No club players available'}
                    </Typography>
                  </div>
                )}
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-medium">
                    <div className="flex items-center space-x-2">
                      <Typography variant="caption" className="text-text-muted">
                        Showing {startIndex + 1}-{Math.min(endIndex, filteredClubPlayers.length)} of {filteredClubPlayers.length} players
                      </Typography>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="px-3 py-1 text-sm text-text-secondary">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Guest Tab */}
          {activeTab === 'guest' && (
            <form onSubmit={handleGuestSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="form-label mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={guestForm.firstName}
                    onChange={(e) => setGuestForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full form-input focus-ring"
                    required
                  />
                </div>
                
                <div>
                  <label className="form-label mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={guestForm.lastName}
                    onChange={(e) => setGuestForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full form-input focus-ring"
                    required
                  />
                </div>
                
                <div>
                  <label className="form-label mb-1">
                    Gender *
                  </label>
                  <select
                    value={guestForm.gender}
                    onChange={(e) => setGuestForm(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full form-input focus-ring"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Adding...' : 'Add Guest Player'}
                </Button>
              </div>
            </form>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div>
              <div className="mb-4">
                <label className="form-label mb-1">
                  Email Address
                </label>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    placeholder="Enter player's email..."
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    className="flex-1 form-input focus-ring"
                  />
                  <Button onClick={handleEmailSearch} disabled={loading}>
                    Search
                  </Button>
                </div>
              </div>
              
              {emailResult && (
                <div className="p-4 bg-surface-subtle rounded-lg border border-border-light">
                  <div className="flex items-center justify-between">
                    <div>
                      <Typography variant="body" className="font-medium">
                        {emailResult.first_name} {emailResult.last_name}
                      </Typography>
                      <Typography variant="caption" className="text-text-muted">
                        {emailResult.email}
                      </Typography>
                    </div>
                    <Button
                      size="small"
                      onClick={handleEmailPlayerAdd}
                      disabled={loading}
                    >
                      Add Player
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-border-medium">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>

        {/* Messages */}
        {error && (
          <div className="px-6 pb-4">
            <div className="bg-danger-light border border-danger text-danger-dark px-4 py-3 rounded-md">
              {error}
            </div>
          </div>
        )}
        
        {success && (
          <div className="px-6 pb-4">
            <div className="bg-success-light border border-success-dark text-success-dark px-4 py-3 rounded-md">
              {success}
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AddPlayerModal;
